import * as React from "react"
import { createPortal } from "react-dom"
import { Link, router, usePage } from "@inertiajs/react"
import {
  ArrowUpRight,
  CalendarCheck,
  ChevronsLeft,
  ChevronsRight,
  Inbox,
  LogOut,
  Menu,
  Settings,
  Shield,
  User,
  X,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ArmedIndicator } from "@/components/ArmedIndicator"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

const STORAGE_KEY = "main-nav-open"
const BRAND = "ReplyOps"

export type SubNavItemDef = {
  href: string
  label: string
  adminOnly?: boolean
  match: (url: string) => boolean
}

export type NavItemDef = {
  href: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  adminOnly?: boolean
  // External links render as a plain <a target="_blank"> and never match a url.
  external?: boolean
  match: (url: string) => boolean
  submenu?: SubNavItemDef[]
}

const SETTINGS_SUBMENU: SubNavItemDef[] = [
  {
    href: "/settings",
    label: "General",
    match: (url) => url === "/settings",
  },
  {
    href: "/settings/clients",
    label: "Clients",
    adminOnly: true,
    match: (url) => url.startsWith("/settings/clients"),
  },
  {
    href: "/settings/classifier",
    label: "Classifier",
    adminOnly: true,
    match: (url) => url.startsWith("/settings/classifier"),
  },
  {
    href: "/settings/checklist_templates",
    label: "Checklist templates",
    adminOnly: true,
    match: (url) => url.startsWith("/settings/checklist_templates"),
  },
  {
    href: "/settings/crm_fields",
    label: "CRM fields",
    adminOnly: true,
    match: (url) => url.startsWith("/settings/crm_fields"),
  },
]

export const TWENTY_CRM_URL = "https://opslab.twenty.com"

// Slimmed global nav (inbox redesign, 2026-08-09): Home and the internal CRM
// submenu are gone from the rail — their routes still exist for deep links.
// CRM lives in Twenty now, reachable via the external link.
const DEFAULT_NAV_ITEMS: NavItemDef[] = [
  {
    href: "/inbox",
    icon: Inbox,
    label: "Inbox",
    match: (url) => url.startsWith("/inbox"),
  },
  {
    href: "/today",
    icon: CalendarCheck,
    label: "Today",
    match: (url) => url.startsWith("/today") || url.startsWith("/my-day"),
  },
  {
    href: "/settings",
    icon: Settings,
    label: "Settings",
    match: (url) => url.startsWith("/settings"),
    submenu: SETTINGS_SUBMENU,
  },
  {
    href: TWENTY_CRM_URL,
    icon: ArrowUpRight,
    label: "Twenty CRM",
    external: true,
    match: () => false,
  },
]

function useMainNavOpen() {
  const [open, setOpen] = React.useState<boolean>(true)
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored !== null) setOpen(stored === "true")
  }, [])
  React.useEffect(() => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(STORAGE_KEY, String(open))
  }, [open])
  return [open, setOpen] as const
}

export function MainNav({
  items = DEFAULT_NAV_ITEMS,
  brandHref = "/inbox",
}: {
  items?: NavItemDef[]
  brandHref?: string
} = {}) {
  const [open, setOpen] = useMainNavOpen()
  const [mobileOpen, setMobileOpen] = React.useState(false)

  return (
    <>
      <aside
        className={cn(
          "sticky top-0 z-30 hidden h-screen shrink-0 flex-col border-r border-hairline bg-page transition-[width] duration-200 lg:flex",
          open ? "w-56" : "w-14",
        )}
      >
        <RailBody
          open={open}
          onToggle={() => setOpen(!open)}
          items={items}
          brandHref={brandHref}
        />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-ink-display/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-page shadow-xl">
            <div className="shrink-0 space-y-3 border-b border-hairline p-4">
              <div className="flex items-center justify-between">
                <span className="font-display text-sm font-semibold text-ink-display">
                  {BRAND}
                </span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
                  aria-label="Close navigation"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <ArmedIndicator />
            </div>
            <RailNav open items={items} onClose={() => setMobileOpen(false)} />
            <div className="border-t border-hairline p-2">
              <UserMenu open />
            </div>
          </aside>
        </div>
      )}

      <div className="fixed right-3 top-3 z-30 flex items-center gap-2 lg:hidden">
        <ArmedIndicator />
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-hairline bg-page text-ink-body hover:bg-surface"
          aria-label="Open navigation"
        >
          <Menu className="h-4 w-4" />
        </button>
      </div>
    </>
  )
}

function RailBody({
  open,
  onToggle,
  items,
  brandHref,
}: {
  open: boolean
  onToggle: () => void
  items: NavItemDef[]
  brandHref: string
}) {
  return (
    <>
      <div
        className={cn(
          "flex h-14 shrink-0 items-center gap-3 border-b border-hairline px-3",
          open ? "justify-between" : "justify-center",
        )}
      >
        {open && (
          <Link
            href={brandHref}
            className="flex min-w-0 items-center gap-2 text-ink-display no-underline"
            aria-label={BRAND}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-faded font-display text-sm font-semibold text-accent">
              {BRAND.charAt(0)}
            </span>
            <span className="truncate font-display text-sm font-semibold">
              {BRAND}
            </span>
          </Link>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-surface hover:text-ink-display"
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
        >
          {open ? (
            <ChevronsLeft className="h-4 w-4" />
          ) : (
            <ChevronsRight className="h-4 w-4" />
          )}
        </button>
      </div>

      <div className={cn("border-b border-hairline p-2", open ? "" : "py-3")}>
        <ArmedIndicator compact={!open} />
      </div>

      <RailNav open={open} items={items} />

      <div className="border-t border-hairline p-2">
        <UserMenu open={open} />
      </div>
    </>
  )
}

function RailNav({
  open,
  items,
  onClose,
}: {
  open: boolean
  items: NavItemDef[]
  onClose?: () => void
}) {
  const { url } = usePage()
  const { props } = usePage<PageProps>()
  const isAdmin = !!props.current_user?.admin
  const visibleItems = items.filter((item) => !item.adminOnly || isAdmin)
  return (
    <nav
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-1 p-2 text-sm",
        open ? "overflow-x-hidden overflow-y-auto" : "overflow-visible",
      )}
    >
      {visibleItems.map((item) => (
        <NavItem
          key={item.href}
          href={item.href}
          icon={item.icon}
          label={item.label}
          active={item.match(url)}
          open={open}
          external={item.external}
          onClick={onClose}
          submenu={item.submenu
            ?.filter((sub) => !sub.adminOnly || isAdmin)
            .map((sub) => ({ ...sub, active: sub.match(url) }))}
        />
      ))}
    </nav>
  )
}

function NavItem({
  href,
  icon: Icon,
  label,
  active,
  open,
  external,
  onClick,
  submenu,
}: {
  href: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  active?: boolean
  open: boolean
  external?: boolean
  onClick?: () => void
  submenu?: (SubNavItemDef & { active: boolean })[]
}) {
  const hasSubmenu = !!submenu && submenu.length > 0
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const [flyoutOpen, setFlyoutOpen] = React.useState(false)
  const [flyoutRect, setFlyoutRect] = React.useState<{ left: number; top: number } | null>(null)
  const [mounted, setMounted] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  const positionFlyout = React.useCallback(() => {
    const rect = wrapperRef.current?.getBoundingClientRect()
    if (!rect) return
    setFlyoutRect({ left: rect.right + (open ? 4 : 13), top: rect.top + rect.height / 2 })
  }, [open])

  const openFlyout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    positionFlyout()
    setFlyoutOpen(true)
  }
  const scheduleCloseFlyout = () => {
    closeTimeoutRef.current = setTimeout(() => setFlyoutOpen(false), 150)
  }
  const closeFlyoutImmediately = () => setFlyoutOpen(false)

  return (
    <div
      ref={wrapperRef}
      className="group/nav-item relative"
      onMouseEnter={hasSubmenu ? openFlyout : undefined}
      onMouseLeave={hasSubmenu ? scheduleCloseFlyout : undefined}
      onFocus={hasSubmenu ? openFlyout : undefined}
      onBlur={hasSubmenu ? closeFlyoutImmediately : undefined}
    >
      {external ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={onClick}
          aria-label={open ? undefined : label}
          className={cn(
            "flex items-center gap-3 rounded-md no-underline",
            open ? "px-3 py-2" : "mx-auto h-9 w-9 justify-center",
            "text-ink-body hover:bg-surface hover:text-ink-display",
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {open && <span className="truncate">{label}</span>}
        </a>
      ) : (
        <Link
          href={href}
          onClick={onClick}
          aria-label={open ? undefined : label}
          aria-haspopup={hasSubmenu ? "menu" : undefined}
          className={cn(
            "flex items-center gap-3 rounded-md no-underline",
            open ? "px-3 py-2" : "mx-auto h-9 w-9 justify-center",
            active
              ? "bg-accent-faded text-accent-display"
              : "text-ink-body hover:bg-surface hover:text-ink-display",
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {open && <span className="truncate">{label}</span>}
        </Link>
      )}
      {!open && !hasSubmenu && (
        <span
          role="tooltip"
          className="pointer-events-none absolute left-full top-1/2 z-50 ml-[13px] -translate-y-1/2 whitespace-nowrap rounded-md border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-sm transition-opacity group-hover/nav-item:opacity-100"
        >
          {label}
        </span>
      )}
      {hasSubmenu && mounted && flyoutOpen && flyoutRect &&
        createPortal(
          <div
            role="menu"
            style={{ left: flyoutRect.left, top: flyoutRect.top }}
            className="fixed z-50 min-w-[12rem] -translate-y-1/2 rounded-md border border-hairline bg-page p-1 text-ink-body shadow-lg"
            onMouseEnter={openFlyout}
            onMouseLeave={scheduleCloseFlyout}
          >
            <div className="px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-ink-muted">
              {label}
            </div>
            {submenu.map((sub) => (
              <Link
                key={sub.href}
                href={sub.href}
                onClick={onClick}
                aria-current={sub.active ? "page" : undefined}
                className={cn(
                  "flex items-center rounded-sm px-2 py-1.5 text-sm no-underline",
                  sub.active
                    ? "bg-accent-faded text-accent-display"
                    : "text-ink-body hover:bg-surface hover:text-ink-display",
                )}
              >
                {sub.label}
              </Link>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}

export function UserMenu({ open }: { open: boolean }) {
  const { props } = usePage<PageProps>()
  const email = props.current_user?.email ?? ""
  const isAdmin = props.current_user?.admin ?? false
  const initial = email.charAt(0).toUpperCase() || "?"

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={open ? undefined : email || "Account"}
          className={cn(
            "group/user relative flex w-full cursor-pointer items-center gap-3 rounded-md text-left text-ink-body hover:bg-surface hover:text-ink-display focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            open ? "px-2 py-2" : "h-10 justify-center",
          )}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-faded text-xs font-semibold text-accent">
            {initial}
          </span>
          {open ? (
            <span className="min-w-0 flex-1 truncate text-sm">{email}</span>
          ) : (
            <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-hairline bg-page px-2 py-1 text-xs font-medium text-ink-display opacity-0 shadow-md transition-opacity group-hover/user:opacity-100">
              {email}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <span className="block text-[10px] uppercase tracking-wider text-ink-muted">
            Signed in as
          </span>
          <span className="block truncate text-xs font-medium text-ink-display">
            {email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/profile" className="no-underline">
            <User /> Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/settings" className="no-underline">
            <Settings /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-2">
          <ThemeToggle block />
        </div>
        <DropdownMenuSeparator />
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link href="/admin/users" className="no-underline">
              <Shield /> Admin
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            router.delete("/logout")
          }}
        >
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
